/**
 * Os terminais de um componente do diagrama — a geometria.
 *
 * **O que esperava o desenho.** Altura, deslocar, posição e ramificação só fazem
 * sentido com o traço ao lado: escolher altura olhando para uma tabela é chutar.
 * Com `desenhar/previa` mostrando como ficaria, passam a fazer.
 *
 * **`PUT componentes/{id}/terminais` é substituição completa** — dos sete campos
 * de cada terminal, mais a cor e o desenho interno do componente, mais a ordem
 * das duas colunas. Mandar meio corpo zera a outra metade. Por isso a tela lê
 * tudo antes (`GET .../terminais`, que não existia) e devolve tudo, inclusive o
 * que não mostra.
 *
 * **Duas sequências de ordem, não uma.** `posicao = "ligacao"` é uma coluna,
 * `"oposto"` é a outra, e o servidor renumera cada uma a partir de 1 na ordem em
 * que os ids chegam. Uma lista só, misturada, embaralharia as duas.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { api, noProjeto, obter } from "./cliente"

/**
 * `circuito.tipo` — os nomes são os do `Tipo_circuito` do legado, na ordem da
 * tela do legado: as linhas 30, 15, 50 e acessórios logo depois dos positivos.
 */
export const TIPOS_DE_CIRCUITO: ReadonlyArray<readonly [number, string]> = [
  [0, "Normal"], [1, "Aterramento"], [2, "Negativo 0V"], [3, "Positivo 12V"],
  [4, "Positivo 5V"], [11, "Positivo linha 30"], [12, "Positivo linha 15"],
  [13, "Positivo linha 50"], [14, "Positivo acessórios"], [5, "Sinal"],
  [6, "Rede CAN H"], [7, "Rede CAN L"], [8, "Rede MS CAN H"], [9, "Rede MS CAN L"],
  [10, "Rede serial"], [15, "Sinal de rádio"], [16, "Alta tensão"],
]

export type TerminalDoComponente = {
  id: number
  pinoutId: number
  ligacaoPinoutId: number
  /** O pino deste lado. Não é gravado — serve para o operador se achar. */
  pino: string
  pinoDaLigacao: string
  codigoDoOutro: string
  descricaoDoOutro: string
  texto: string
  altura: number
  deslocar: boolean
  posicao: string
  ordem: number
  ramificacao: number
  ramificacaoPosicao: number
  ocultar: boolean
  juncao: boolean
  componenteDaLigacao: number
  circuitoId: number
  tipoCircuito: number
  apelidoCircuito: string
  /** `posicao === "ligacao"`: diz em qual das duas sequências de ordem ele está. */
  naColunaLigacao: boolean
  /**
   * Os quatro seguintes não aparecem nesta tela: existem porque
   * `PUT ligacoes/{id}` é substituição completa, e o editor gráfico precisa
   * devolvê-los como estão. Sem eles, mexer numa altura desfazia a ramificação e
   * a junção do terminal em silêncio.
   */
  ramificacaoId: number
  textoDescricao: string
  juncaoDiagramaComponenteId: number
  juncaoPinoutId: number
}

export function usarTerminaisDoComponente(diagramaComponenteId: number) {
  return useQuery({
    queryKey: ["componente-terminais", diagramaComponenteId] as const,
    enabled: diagramaComponenteId > 0,
    staleTime: 10_000,
    queryFn: () => obter<TerminalDoComponente[]>(
      `api/diagramas/componentes/${diagramaComponenteId}/terminais`, {}),
  })
}

/**
 * Os terminais cujo pino aparece mais de uma vez na mesma caixa — os que o
 * "Remover pino repetido" (o `DiagramaComponenteAgrupar` do legado) tem o que
 * fazer. Agrupados por pino, na ordem em que vieram.
 *
 * Junção fica de fora, como na lista do legado, que só olhava as ligações
 * comuns. O legado ainda tinha um defeito: a condição que mostrava o item no
 * menu contava o próprio terminal como repetido, e o item aparecia sempre.
 */
export function pinosRepetidos(terminais: TerminalDoComponente[]): TerminalDoComponente[] {
  const comuns = terminais.filter((t) => !t.juncao && t.pinoutId > 0)
  const vezes = new Map<number, number>()
  for (const t of comuns) vezes.set(t.pinoutId, (vezes.get(t.pinoutId) ?? 0) + 1)
  const repetidos = comuns.filter((t) => (vezes.get(t.pinoutId) ?? 0) > 1)
  const ordemDoPino = [...new Set(repetidos.map((t) => t.pinoutId))]
  return repetidos.sort((a, b) => ordemDoPino.indexOf(a.pinoutId) - ordemDoPino.indexOf(b.pinoutId))
}

/** O que o `PUT` grava de cada terminal. */
export type TerminalParaGravar = {
  diagramaLigacaoId: number
  texto: string
  altura: number
  deslocar: boolean
  ramificacaoPosicao: number
  posicao: string
  tipoCircuito: number
  apelidoCircuito: string
}

/**
 * **Substituição completa.** `cor` e `desenhoInternoId` são do componente, não do
 * terminal, e vão no mesmo corpo: omiti-los limparia a cor e o desenho interno
 * de quem só queria mexer numa altura.
 */
export function usarEditarTerminais(projetoId: number, diagramaId: number, componenteId: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (corpo: {
      cor: string
      desenhoInternoId: number
      terminais: TerminalParaGravar[]
      ordem: { diagramaLigacaoId: number; ligacao: boolean }[]
    }) => {
      const r = await api.put<{ ok: boolean; componentesParaAtualizar: number[] }>(
        `api/diagramas/componentes/${componenteId}/terminais`, corpo, noProjeto(projetoId))
      return r.data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["componente-terminais", componenteId] })
      qc.invalidateQueries({ queryKey: ["diagrama-componentes", diagramaId] })
      qc.invalidateQueries({ queryKey: ["diagrama-desenho-estado", diagramaId] })
      qc.invalidateQueries({ queryKey: ["diagramas", projetoId] })
    },
  })
}
